<template>
  <div class="email-tags-container">
    <div class="email-tags" @click="focusInput">
      <span v-for="(email, index) in emails" :key="index" class="email-tag">
        {{ email }}
        <button type="button" class="tag-remove" @click="removeEmail(index)">
          <CIcon name="cilX" size="sm"/>
        </button>
      </span>
      <input
        ref="emailInput"
        v-model="currentInput"
        type="text"
        class="tag-input"
        :placeholder="emails.length === 0 ? placeholder : ''"
        @keydown.enter.prevent="handleAddEmail"
        @keydown.backspace="handleBackspace"
        @paste="handlePaste"
      />
    </div>
    <div v-if="emailError" class="text-danger small mt-1">
      {{ emailError }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'PEmailTagInput',

  props: {
    value: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'email@example.com'
    }
  },

  emits: ['update:value', 'input'],

  data() {
    return {
      emails: [],
      currentInput: '',
      emailError: ''
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (Array.isArray(newValue)) {
          this.emails = [...newValue]
        }
      }
    }
  },

  methods: {
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },

    handleAddEmail() {
      if (!this.currentInput.trim()) return

      const emailsToAdd = this.currentInput
        .split(/[\n,;]+/)
        .map(e => e.trim())
        .filter(e => e)

      const validEmails = []
      const invalidEmails = []
      const duplicateEmails = []

      for (const email of emailsToAdd) {
        if (!this.validateEmail(email)) {
          invalidEmails.push(email)
        } else if (this.emails.includes(email)) {
          duplicateEmails.push(email)
        } else {
          validEmails.push(email)
        }
      }

      // Add valid emails
      if (validEmails.length > 0) {
        this.emails.push(...validEmails)
        this.emitUpdate()
      }

      // Show errors if present
      if (invalidEmails.length > 0) {
        this.emailError = this.$pgettext('email_validation', 'Invalid email address: ') + invalidEmails.join(', ')
      } else if (duplicateEmails.length > 0) {
        this.emailError = this.$pgettext('email_validation', 'Email already added: ') + duplicateEmails.join(', ')
      } else {
        this.emailError = ''
      }

      this.currentInput = ''
    },

    removeEmail(index) {
      this.emails.splice(index, 1)
      this.emitUpdate()
    },

    handleBackspace() {
      if (this.currentInput === '' && this.emails.length > 0) {
        this.emails.pop()
        this.emitUpdate()
      }
    },

    handlePaste(event) {
      event.preventDefault()
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')
      this.currentInput = pastedText
      this.handleAddEmail()
    },

    focusInput() {
      this.$refs.emailInput.focus()
    },

    emitUpdate() {
      this.$emit('update:value', this.emails)
      this.$emit('input', this.emails)
    }
  }
}
</script>

<style scoped>
.email-tags-container {
  position: relative;
}

.email-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 38px;
  padding: 4px;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  background: white;
  cursor: text;
}

.email-tags:focus-within {
  border-color: #768192;
  box-shadow: 0 0 0 0.2rem rgba(39, 41, 43, 0.15);
}

.email-tag {
  display: inline-flex;
  align-items: center;
  margin: 2px;
  padding: 4px 8px;
  background: #f0f3f5;
  border: 1px solid #d8dbe0;
  border-radius: 3px;
  font-size: 14px;
}

.tag-remove {
  margin-left: 6px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #768192;
  display: inline-flex;
  align-items: center;
}

.tag-remove:hover {
  color: #e55353;
}

.tag-input {
  flex: 1;
  min-width: 200px;
  margin: 2px;
  padding: 4px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
}
</style>
